import React, { useState, useCallback, useEffect, useRef } from 'react'
import { antdFormError, borderColumnColor, borderColumnErrorColor, borderColumnWidth, columnDefaultDisposition, columnsDispositions, containerBackground, ItemTypes, pageBackground, roundButtonWidth, viewerTypes } from '../../constants/constants'
import AdderColumns from '../Container/AdderColumns'
import { useStore } from '../../store/store'
import { useDrop } from 'react-dnd'
import { isAOneRowBox, isATableBox } from '../../utils/boxes'
import { getContainerByColumnId, getColumnById, getBoxesByColumnId } from '../../store/lookups'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { containerColumnGap } from '../../utils/columns'
import { Button } from 'antd'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'

// Entre deux colonnes, chaque bande couvre la moitié de l'écart côté de sa
// colonne : les deux se rejoignent au milieu, où se pose le « + ».
const getWidthAndPosition = ({ position, columnIndex, willBoxBeReplaced, containerColumnsLength, innerWidth }) => {
  if (position === "left") {
    if (columnIndex === 0 ||
      willBoxBeReplaced) {
      return borderColumnWidth
    } else {
      return innerWidth
    }
  } else {
    if (columnIndex + 1 === containerColumnsLength ||
      willBoxBeReplaced) {
      return borderColumnWidth
    } else {
      return innerWidth
    }
  }
}

const borderCommonStyle = ({
  isVisible,
  position,
  willBoxBeReplaced,
  boxCanDropInColumn,
  isDragging,
  containerColumnsLength,
  columnIndex,
  innerWidth,
}) => {
  const style = {
    position: 'absolute',
    height: "100%",
    backgroundColor: boxCanDropInColumn || !isDragging ? borderColumnColor : borderColumnErrorColor,
    top: 0,
    opacity: isVisible ? 1 : 0,
    transition: 'opacity 200ms ease',
    display: "flex"
  }

  if (position === "left") {
    if (columnIndex === 0 ||
      willBoxBeReplaced) {
      style.width = borderColumnWidth
    } else {
      style.width = innerWidth
    }
  } else {
    if (columnIndex + 1 === containerColumnsLength ||
      willBoxBeReplaced) {
      style.width = borderColumnWidth
    } else {
      style.width = innerWidth
    }
    style.right = 0
  }

  return style
}

const selector = (columnId) => ({
  containers,
  updateColumnsInContainer,
  selectedBoxId,
  boxes,
  boxVariableSelectedId,
  currentProposal,
  selectedContainer,
  updateContainer,
  deleteColumns,
  columns
}) => {
  const container = getContainerByColumnId(containers, columnId);
  // const box = boxes?.find((b) => b.columnId === columnId);
  return {
    container,
    updateColumnsInContainer,
    columnIndex: container.columnsIds.findIndex((id) => id === columnId),
    isBoxSelected: !!selectedBoxId && boxes.some((b) => selectedBoxId === b.id && container.columnsIds.includes(columnId)),
    isATableBoxSelected: !!selectedBoxId && isATableBox(boxes.find(b => b.id === selectedBoxId)?.type),
    boxVariableSelectedId,
    currentProposal,
    selectedBoxId,
    boxes,
    box: getBoxesByColumnId(boxes, columnId)[0],
    isContainerSelected: selectedContainer && selectedContainer.id === container.id,
    selectedContainer,
    updateContainer,
    deleteColumns,
    column: getColumnById(columns, columnId),
    columnsInContainer: columns.filter((c) => container.columnsIds.includes(c.id)),
  }
};

const BorderColumn = ({
  position,
  willBoxBeReplaced,
  containerColumnsLength,
  columnId,
  dropCommonProps,
  isContainerHovered,
  boxCanDropInColumn,
  hasAOneRowBox,
  setControlCol,
  setControlActionHovered,
  setDeleteButtonHovered,
  controlCol,
  deleteButtonHovered,
  controlActionHovered,
  isLastColumn,
  isFirstColumn,
  isMiddleColumn,
  setControlRowClicked,
  fromViewer
}) => {
  const {
    columnIndex,
    container,
    updateColumnsInContainer,
    isBoxSelected,
    boxVariableSelectedId,
    currentProposal,
    isContainerSelected,
    box,
    selectedContainer,
    updateContainer,
    deleteColumns,
    column,
    isATableBoxSelected,
  } = useStore(selector(columnId));
  const [isHovered, setIsHovered] = useState(false)

  const onBorderDrop = (box) => {
    const columnsDisposition = columnsDispositions.find((cd) => {
      return cd.key === container.columnsIds.length
    });
    const columnsSizes = columnsDisposition.dispositions.find((d) => {
      return d.sizes.every((size) => size === d.sizes[0])
    }).sizes
    updateColumnsInContainer({
      columnsSizes,
      index: position === 'right' ? columnIndex : null,
      containerId: container.id,
      newBoxes: [box]
    });
  }

  const showColumn = position === 'right' || columnIndex === 0

  const canAddColumn =
    // !isBoxSelected &&
    container.columnsIds.length < 4 &&
    showColumn &&
    // If dropped box take full size
    !hasAOneRowBox

  const boxCanDropBetweenColumn = (_, monitor) => {
    const box = monitor.getItem();
    // If dragged box need full size
    if (isAOneRowBox(box.type)) {
      return false;
    }
    if (!boxCanDropInColumn) return false;
    if (!canAddColumn) return false;
    return true
  }

  const [{ boxIsOver, canDrop, isDragging, isDragActive }, dropColumnBorder] = useDrop({
    ...dropCommonProps,
    collect: (monitor) => ({
      ...dropCommonProps.collect(monitor),
      // Tout glisser en cours, pas seulement une nouvelle box (isDragging).
      isDragActive: monitor.getItem() !== null,
    }),
    canDrop: boxCanDropBetweenColumn,
    drop: onBorderDrop,
  });

  const isVisible = !!(isContainerHovered || isDragging || willBoxBeReplaced || isContainerSelected)

  // Bande d'au moins 1px : à écart nul, elle resterait survolable.
  const innerWidth = Math.max(containerColumnGap(container) / 2, 1)
  const boderPosition = position === 'left' ? {
    left: -getWidthAndPosition({ position, columnIndex, willBoxBeReplaced, containerColumnsLength, innerWidth })
  } : { right: -getWidthAndPosition({ position, columnIndex, willBoxBeReplaced, containerColumnsLength, innerWidth }) }

  const transform = () => {
    if (columnIndex === 0 && position === 'left') return 2;
    if (columnIndex + 1 === containerColumnsLength && position === 'right') return 30 - 2
    return 30
  }

  const zIndexBoder = () => {
    if (!isHovered && !isDragging) return 0
    return position === "right" ? 3 : 2
  }

  const canAddColumnOnClick = canAddColumn && !isDragging
  const canDisplayBorderColumn = useCallback(() => {
    if (currentProposal.id === null && container?.from_linked_section) return false;
    if (box?.type === ItemTypes.SPACE) return false
    if (currentProposal.id === null) return true
    if (currentProposal.id !== null && !container.hasOwnProperty('editable')) return true
    if (currentProposal.id !== null && container?.editable) return true
    else return false
  }, [isDragging, isBoxSelected, canAddColumn, box])

  const divRef = useRef(null);

  const handleClickOutside = useCallback((event) => {
    if (divRef.current && !divRef.current.contains(event.target)) {
      setControlCol(null);
    }
  }, [setControlCol, divRef]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const controlActionColor = () => {
    if (deleteButtonHovered) return "red"
    if (controlCol || controlActionHovered) return "#c1e7ff"
    else return "#efefef"
  }
  return (
    <>
      {fromViewer !== viewerTypes.VARIABLES && isContainerSelected && !isATableBoxSelected && (isFirstColumn || isLastColumn || isMiddleColumn) && (
        <div
          ref={divRef}
          data-outside-ignore="true"
          data-border-band="true"
          data-column-id={columnId}
          style={{
            position: "absolute",
            left: -2,
            top: -18,
            width: "calc(100% + 4px)",
            height: 20,
            backgroundColor: "transparent",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
          onMouseEnter={() => setControlActionHovered(true)}
          onMouseLeave={() => setControlActionHovered(false)}
          onClick={(e) => {
            e.stopPropagation();
            setControlCol(columnId)
          }}
        >
          {controlCol && !column?.from_linked_section && <div>
            <Button
              isFromHeader
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                padding: 0,
                position: "absolute",
                top: -15,
                marginLeft: -10,
                backgroundColor: isHovered ? antdFormError : undefined,
                borderColor: isHovered ? "white" : undefined,
                borderRadius: 20
              }}
              icon={<FontAwesomeIcon icon={faXmark}
                style={{
                  transition: "color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
                  color: isHovered ? "white" : "rgba(0, 0, 0, 0.88)",
                  display: 'block',
                }} />}
              setControlRowClicked={setControlRowClicked}
              onMouseEnter={() => setDeleteButtonHovered(true)}
              onMouseLeave={() => setDeleteButtonHovered(false)}
              data-outside-ignore="true"
              onClick={(e) => {
                e.stopPropagation();
                updateContainer({ id: selectedContainer.id, fn: c => c.columnsIds = c.columnsIds.filter(cId => cId !== columnId) })
                updateColumnsInContainer({
                  containerId: selectedContainer.id,
                  columnsSizes: columnDefaultDisposition[selectedContainer.columnsIds.length - 1],
                  index: columnIndex
                })
                deleteColumns([columnId])
              }}
            /></div>}
          <div
            role="button"
            tabIndex={0}
            style={{
              width: "100%", height: 10, backgroundColor: controlActionColor(),
              border: "1px solid black",
              borderRadius: `${isFirstColumn ? 4 : 0}px ${isLastColumn ? 4 : 0}px 0 0`,
              borderLeft: isFirstColumn ? "display" : "none",
              position: "absolute",
              bottom: 0,
              cursor: "pointer"
            }}
            data-outside-ignore="true"
            onClick={(e) => {
              e.stopPropagation();
              setControlCol((prev) => (prev === columnId ? null : columnId));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setControlCol((prev) => (prev === columnId ? null : columnId));
              }
            }}
          />
        </div>
      )}
      <div
        ref={dropColumnBorder}
        data-role={pageBackground}
        data-parent={containerBackground}
        style={{
          position: 'absolute',
          height: "100%",
          top: 0,
          display: "flex",
          width: roundButtonWidth,
          // La bande déborde d'environ 26px dans la colonne et passe au-dessus
          // de son contenu : hors glisser, elle volait le survol des « × »
          // posés dans le coin haut droit d'une ligne ou d'une cellule (la
          // ligne se croyait quittée et masquait son bouton). Elle ne sert de
          // cible qu'au drop ; le « + » d'ajout de colonne garde ses clics.
          pointerEvents: isDragActive ? undefined : 'none',
          ...boderPosition
        }}
      >
        <div
          data-role={pageBackground}
          data-parent={containerBackground}
          style={{
            ...borderCommonStyle({
              isVisible,
              position,
              willBoxBeReplaced,
              boxCanDropInColumn,
              isDragging,
              containerColumnsLength,
              columnIndex,
              innerWidth,
            }),
          }} />
        {(canAddColumnOnClick || canDrop) && !boxVariableSelectedId &&
          <div style={{
            zIndex: 2,
            pointerEvents: 'auto',
            transform: `translateX(calc(-50% + ${transform()}px))`,
          }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            {canDisplayBorderColumn() && <AdderColumns
              columnId={columnId}
              columnIndex={position === 'right' ? columnIndex : null}
              boxIsOver={boxIsOver}
              canDrop={canDrop}
              defaultFontSizeIcon={15}
            />}
          </div>
        }
      </div>
    </>
  )
}

export default BorderColumn;
